import { inject, Injectable, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { HttpClient } from '@angular/common/http';
import { catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private transloco = inject(TranslocoService);
  private http = inject(HttpClient);
  private readonly storageKey = 'userLanguage';
  // Всегда адрес бэкенда. Раньше при application === 'DEVELOPMENT' брался
  // относительный путь: он рассчитан на proxy.conf.json при ng serve, но в
  // собранном dev-образе уходил на хост самого фронта и отдавал 405.
  private readonly changeLanguageUrl = `${environment.baseUrl}/change-language/`;

  // Map frontend language codes to backend codes
  private languageCodeMap: { [key: string]: string } = {
    kz: 'kk', // Frontend uses 'kz', backend uses 'kk'
    ru: 'ru',
    tr: 'tr',
    en: 'en',
    kk: 'kk', // Also support 'kk' from backend
  };
  languages: Array<{
    name: string;
    code: string;
  }> = [
    {
      name: 'Қазақ',
      code: 'kz',
    },
    {
      name: 'Русский',
      code: 'ru',
    },
    {
      name: 'Türkçe',
      code: 'tr',
    },
    {
      name: 'English',
      code: 'en',
    },
  ];

  activeLang = signal<string>(this.getInitialLanguage());

  private getInitialLanguage(): string {
    const storedLang = localStorage.getItem(this.storageKey);

    return storedLang || this.getMatchingBrowserLanguage() || 'ru';
  }

  private getMatchingBrowserLanguage(): string | null {
    const browserLang = navigator.language.split('-')[0];
    return this.languages.some((lang) => lang.code === browserLang)
      ? browserLang
      : null;
  }

  toggleLanguageByCode(langCode: string, updateBackend: boolean = true) {
    // Normalize the language code (backend might send 'kk', we use 'kz' in frontend)
    let normalizedCode = langCode;
    if (langCode === 'kk') {
      normalizedCode = 'kz';
    }

    const langObj = this.languages.find((lang) => lang.code === normalizedCode);
    if (langObj) {
      this.activeLang.set(langObj.code);
      this.transloco.setActiveLang(langObj.code);
      localStorage.setItem(this.storageKey, langObj.code);

      if (updateBackend) {
        const backendCode = this.languageCodeMap[langObj.code] || langObj.code;
        this.http
          .put(this.changeLanguageUrl, { lang: backendCode })
          .pipe(
            tap(() => console.log('Language updated on backend successfully')),
            catchError((error) => {
              console.error('Failed to update language on backend:', error);
              return of(null);
            })
          )
          .subscribe();
      }
    } else {
      // console.log('Language not found in available languages:', langCode);
    }
  }

  getCurrentLanguageName(): string {
    return (
      this.languages.find((lang) => lang.code === this.activeLang())?.name || ''
    );
  }
}
